import { ArrowRight, Boxes } from "lucide-react";

import type { RenderAssetListItem } from "@/lib/api-types";
import {
  getAssetStorefrontLabel,
  getAssetStorefrontToneClass,
  resolveAssetStorefrontSummaryMode,
} from "@/lib/storefront";
import {
  formatRenderCollectionLabel,
  renderAssetPriceLabel,
} from "@/lib/render-catalog";

function priceToneClass(asset: RenderAssetListItem): string {
  const summary = asset.pricingSummary;
  if (!summary) {
    return "border-border bg-muted text-muted-foreground";
  }
  const price = summary.defaultPrice ?? summary.maxPrice ?? summary.minPrice;
  if (price === undefined) {
    return "border-border bg-muted text-muted-foreground";
  }
  if (price <= 0) {
    return "border-amber-500/30 bg-amber-500/10 text-amber-700";
  }
  return "border-emerald-500/30 bg-emerald-500/10 text-emerald-700";
}

export default function RenderCatalogAssetCard({
  asset,
  isSelected,
  onSelect,
}: {
  asset: RenderAssetListItem;
  isSelected: boolean;
  onSelect: (asset: RenderAssetListItem) => void;
}) {
  const storefrontSummaryMode = resolveAssetStorefrontSummaryMode(
    asset.pricingSummary,
  );
  const linkedTextureCount = asset.pricingSummary?.linkedTextureCount ?? 0;
  const missingTextureCount = asset.pricingSummary?.missingTextureCount ?? asset.textureCount;
  const drawableMeshPartCount = Math.max(asset.drawableMeshPartCount ?? 1, 1);

  return (
    <button
      type="button"
      aria-pressed={isSelected}
      onClick={() => onSelect(asset)}
      className={`w-full rounded-2xl border p-3 text-left transition-colors ${
        isSelected
          ? "border-primary/40 bg-primary/10"
          : "border-border/80 bg-card/70 hover:bg-muted/40"
      }`}
    >
      <div className="space-y-3">
        <div className="overflow-hidden rounded-xl border border-border/80 bg-muted/20">
          {asset.previewImageUrl ? (
            <img
              src={asset.previewImageUrl}
              alt={`Preview ${asset.assetName}`}
              className="aspect-[4/5] w-full object-cover"
              loading="lazy"
            />
          ) : (
            <div className="flex aspect-[4/5] w-full items-center justify-center text-[10px] uppercase tracking-[0.18em] text-muted-foreground">
              No preview
            </div>
          )}
        </div>

        <div className="space-y-3">
          <div className="space-y-2">
            <div className="flex flex-wrap items-center gap-2 text-[11px] uppercase tracking-[0.18em] text-muted-foreground">
              <span>{formatRenderCollectionLabel(asset.collectionDisplay)}</span>
              <span>•</span>
              <span>{asset.pedGender}</span>
              <span>•</span>
              <span>{asset.kind}</span>
            </div>
            <div className="flex items-start justify-between gap-3">
              <div className="min-w-0">
                <p className="truncate font-semibold">{asset.assetName}</p>
                <p className="truncate text-xs text-muted-foreground">
                  {asset.slotLabel || asset.slotName} • drawable {asset.drawableLocalIndex} • mesh {asset.meshPartIndex}
                </p>
                {drawableMeshPartCount > 1 ? (
                  <p className="mt-1 text-xs font-medium text-muted-foreground">
                    {drawableMeshPartCount} mesh parts collapsed
                  </p>
                ) : null}
              </div>
              <div className="rounded-full border border-border/80 px-2 py-1 text-[11px] text-muted-foreground">
                slot {asset.slotId}
              </div>
            </div>
          </div>

          <div className="grid gap-2 sm:grid-cols-2">
            <div className="rounded-2xl border border-border/80 bg-muted/20 px-3 py-2">
              <p className="text-[11px] uppercase tracking-[0.18em] text-muted-foreground">
                Storefront
              </p>
              <span
                className={`mt-2 inline-flex rounded-md border px-2 py-1 text-xs font-semibold ${getAssetStorefrontToneClass(storefrontSummaryMode)}`}
              >
                {getAssetStorefrontLabel(storefrontSummaryMode)}
              </span>
            </div>
            <div className="rounded-2xl border border-border/80 bg-muted/20 px-3 py-2">
              <p className="text-[11px] uppercase tracking-[0.18em] text-muted-foreground">
                Price
              </p>
              <span
                className={`mt-2 inline-flex rounded-md border px-2 py-1 text-sm font-semibold ${priceToneClass(asset)}`}
              >
                {renderAssetPriceLabel(asset.pricingSummary)}
              </span>
            </div>
          </div>

          <div className="grid grid-cols-2 gap-2 text-xs text-muted-foreground">
            <div className="rounded-xl border border-border/80 bg-background/60 px-3 py-2">
              <p className="text-[11px] uppercase tracking-[0.18em]">Textures</p>
              <p className="mt-1 text-sm font-medium text-foreground">
                {asset.textureCount}
              </p>
            </div>
            <div className="rounded-xl border border-border/80 bg-background/60 px-3 py-2">
              <p className="text-[11px] uppercase tracking-[0.18em]">Linked</p>
              <p className="mt-1 text-sm font-medium text-foreground">
                {linkedTextureCount}
              </p>
            </div>
            <div className="rounded-xl border border-border/80 bg-background/60 px-3 py-2">
              <p className="text-[11px] uppercase tracking-[0.18em]">Pending</p>
              <p className="mt-1 text-sm font-medium text-foreground">
                {missingTextureCount}
              </p>
            </div>
            <div className="rounded-xl border border-border/80 bg-background/60 px-3 py-2">
              <p className="text-[11px] uppercase tracking-[0.18em]">DDS</p>
              <p className="mt-1 text-sm font-medium text-foreground">
                {asset.ddsCount}
              </p>
            </div>
          </div>

          <div className="flex items-center justify-between rounded-2xl border border-border/80 bg-background/60 px-3 py-2 text-sm text-muted-foreground">
            <div className="flex items-center gap-2">
              <Boxes className="h-4 w-4" />
              <span>Open detail to inspect preview, textures and pricing.</span>
            </div>
            <ArrowRight className="h-4 w-4" />
          </div>
        </div>
      </div>
    </button>
  );
}
